import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AvailityElement,
  AvailityServiceReviewConfiguration,
} from "risa-data-model";
import { Button } from "risa-oasis-ui_v2";
import { RootState } from "../../redux/store/store";
import { InfoIcon } from "../../svg/info-icon";
import {
  filterRequiredAllowedElements,
  populateKeyLabelElements,
} from "../../utils/procedurePaFormHelper";
import {
  activeSectionsList,
  KeyLabelElementObject,
  keyLabelElementObjects,
} from "../../utils/procedurePaFromConstants";
import { LeftSection } from "./components/left-section";
import { renderElement } from "./components/render-elements";
import {
  addAdditionalComponent,
  handleChange,
  handleCheckboxChanges,
  onDeleteClick,
} from "./procedureFormUtils";
import "./styles/procedure-pa-form.scss";

interface ProcedurePaFormProps {
  onConfigDataChange: (configData: AvailityServiceReviewConfiguration) => void;
}

export const ProcedurePaFrom: React.FC<ProcedurePaFormProps> = ({
  onConfigDataChange,
}) => {
  const sectionRefs = useRef({});

  const [keyLabelElements, setKeyLabelElements] = useState<
    KeyLabelElementObject[]
  >([]);
  const [configData, setConfigData] =
    useState<AvailityServiceReviewConfiguration>();
  const [activeSection, setActiveSection] = useState<string>(
    "serviceDetailsHeading",
  );

  const { data: availityServiceReviewConfigurationData } = useSelector(
    (state: RootState) => state.availityServiceReviewConfiguration,
  );

  useEffect(() => {
    if (availityServiceReviewConfigurationData) {
      setConfigData(availityServiceReviewConfigurationData);
      const filteredElements:
        | { [key: string]: AvailityElement }
        | { [key: string]: AvailityElement }[] = filterRequiredAllowedElements(
        availityServiceReviewConfigurationData.elements,
      );

      const data = populateKeyLabelElements(
        filteredElements,
        keyLabelElementObjects,
      );
      setKeyLabelElements(data);
    }
  }, [availityServiceReviewConfigurationData]);

  useEffect(() => {
    if (configData) {
      onConfigDataChange(configData);
    }
  }, [configData, onConfigDataChange]);

  const handleScroll = () => {
    for (let key in sectionRefs.current) {
      const element = sectionRefs.current[key];
      if (element) {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight && rect.bottom >= 0;

        if (isVisible && activeSectionsList.includes(key)) {
          setActiveSection(key);
          break;
        }
      }
    }
  };

  const handleItemClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      setActiveSection(sectionId);
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const renderElements = (keyLabelElements: KeyLabelElementObject[]) =>
    keyLabelElements.map((item) =>
      renderElement(
        sectionRefs,
        item,
        (name, value) =>
          handleChange(name, value, setKeyLabelElements, setConfigData),
        (key) => onDeleteClick(key, setKeyLabelElements, setConfigData),
        (name, value) =>
          handleCheckboxChanges(
            name,
            value,
            setKeyLabelElements,
            setConfigData,
          ),
        (key) =>
          addAdditionalComponent(
            key,
            configData,
            keyLabelElements,
            setKeyLabelElements,
            setConfigData,
          ),
      ),
    );

  return (
    <>
      {!availityServiceReviewConfigurationData ? (
        <>
          <FormConfigErrorComponent />
        </>
      ) : (
        <>
          <div className="paform-page-container w-full">
            <div className="content-container w-full">
              {LeftSection(activeSection, handleItemClick)}
              <div className="form-container" onScroll={handleScroll}>
                {keyLabelElements && renderElements(keyLabelElements)}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const FormConfigErrorComponent = () => {
  const navigate = useNavigate();
  return (
    <div className="error-container flex h-full w-full flex-col items-center justify-center gap-2">
      <div className="error-icon">
        <InfoIcon height={"48"} width={"48"} stroke={"#0056D6"} />
      </div>
      <div className="error-message text-center text-h11 font-semiBold text-primaryGray-1">
        Form configuration details were not found.
        <br />
        Please try again later.
      </div>
      <div className="error-message button mt-4">
        <Button
          disabled={false}
          onClick={() => navigate("/worklists/")}
          buttonType={"primary"}
          size={"medium"}
        >
          Back to Home page
        </Button>
      </div>
    </div>
  );
};
